import { UpcomingTopic } from "@/types/upcomingTopics";
import { responseStatus, shouldShowErrorPage } from "@/utils/apiErrors";
import { onJotaiUserEvent } from "@/utils/EventListener";
import { REBALANCED_SEPERATION_DISTANCE } from "@/utils/positionRebalancing";
import { queryClient } from "@/utils/queryClient";
import { queryOptions, useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useSyncExternalStore } from "react";

export type UpcomingTopicChange = {
    id: UpcomingTopic["id"];
    content?: UpcomingTopic["content"];
    position?: UpcomingTopic["position"];
    is_completed?: boolean;
};

const UPCOMING_TOPICS_KEY = "upcomingTopics";
const SAVE_UPCOMING_TOPICS_MUTATION_KEY = ["upcomingTopics.save"];

/*
 * A failed save is rolled back and refetched below, so it must not replace the
 * chat or a live session with the error page.
 */
const saveMutationOptions = {
    mutationKey: SAVE_UPCOMING_TOPICS_MUTATION_KEY,
    throwOnError: false,
};

/*
 * Never stale: every save below and the UpcomingTopicsModified and
 * ClientCoachCreatedBroadcastEvent broadcasts refresh these queries. Only a
 * failed first load shows the error page; a failed refetch keeps the topics.
 */
export function upcomingTopicsQuery(otherUserId: number) {
    return queryOptions({
        queryKey: [UPCOMING_TOPICS_KEY, otherUserId],
        queryFn: () =>
            axios
                .get<UpcomingTopic[]>(
                    route("upcoming-topic.index", {
                        other_user_id: otherUserId,
                    }),
                )
                .then((response) => response.data),
        staleTime: Infinity,
        throwOnError: (error, query) =>
            query.state.data === undefined && shouldShowErrorPage(error),
    });
}

export const currentCoachUpcomingTopicsQuery = queryOptions({
    queryKey: [UPCOMING_TOPICS_KEY, "currentCoach"],
    queryFn: () =>
        axios
            .get<UpcomingTopic[]>(route("upcoming-topic.index"))
            .then((response) => response.data)
            .catch(noTopicsWhenClientHasNoCoach),
    staleTime: Infinity,
});

function noTopicsWhenClientHasNoCoach(error: unknown): UpcomingTopic[] {
    if (responseStatus(error) === 404) {
        return [];
    }

    throw error;
}

export function refreshUpcomingTopics(): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: [UPCOMING_TOPICS_KEY] });
}

function forgetUpcomingTopicsOnLogout() {
    queryClient.removeQueries({ queryKey: [UPCOMING_TOPICS_KEY] });
}

onJotaiUserEvent(
    "UpcomingTopicsModified",
    () => {
        void refreshUpcomingTopics();
    },
    forgetUpcomingTopicsOnLogout,
);

onJotaiUserEvent("ClientCoachCreatedBroadcastEvent", () => {
    void refreshUpcomingTopics();
});

export function getTopicsHash(topics: UpcomingTopic[]): string {
    return topics
        .map(
            (topic) =>
                `${topic.id}:${topic.position}:${topic.completed_at}:${topic.content}`,
        )
        .join(",");
}

/*
 * Reflects every pair's cached topics, so the popover inviting a coach to add
 * their first topic goes away as soon as one exists for any client.
 */
export function useHasAnyUpcomingTopics(): boolean {
    return useSyncExternalStore(
        (onChange) => queryClient.getQueryCache().subscribe(onChange),
        () =>
            queryClient
                .getQueriesData<UpcomingTopic[]>({
                    queryKey: [UPCOMING_TOPICS_KEY],
                })
                .some(([, topics]) => !!topics?.length),
    );
}

export function useCreateUpcomingTopic(otherUserId: number) {
    const { queryKey } = upcomingTopicsQuery(otherUserId);

    return useMutation({
        ...saveMutationOptions,
        mutationFn: (content: UpcomingTopic["content"]) =>
            axios
                .post<UpcomingTopic>(route("upcoming-topic.create"), {
                    other_user_id: otherUserId,
                    content: content.trim(),
                    position: positionBelowEveryTopic(
                        queryClient.getQueryData(queryKey) ?? [],
                    ),
                })
                .then((response) => response.data),
        onSuccess: (createdTopic) =>
            queryClient.setQueryData(
                queryKey,
                (topics) => topics && [...topics, createdTopic],
            ),
        onSettled: () => refreshTopicsOnceEverySaveIsDone(otherUserId),
    });
}

export function useDeleteUpcomingTopic(otherUserId: number) {
    return useMutation({
        ...saveMutationOptions,
        mutationFn: (topicId: UpcomingTopic["id"]) =>
            axios.delete(
                route("upcoming-topic.delete", {
                    other_user_id: otherUserId,
                    id: topicId,
                }),
            ),
        onMutate: (topicId) =>
            updateTopicsOptimistically(otherUserId, (topics) =>
                topics.filter((topic) => topic.id !== topicId),
            ),
        onError: (_error, _topicId, topicsBeforeSave) =>
            restoreTopics(otherUserId, topicsBeforeSave),
        onSettled: () => refreshTopicsOnceEverySaveIsDone(otherUserId),
    });
}

export function useUpdateUpcomingTopics(otherUserId: number) {
    return useMutation({
        ...saveMutationOptions,
        mutationFn: (topicChanges: UpcomingTopicChange[]) =>
            axios.patch(route("upcoming-topic.update-many"), {
                other_user_id: otherUserId,
                upcoming_topics: topicChanges,
            }),
        onMutate: (topicChanges) => {
            const changesById = new Map(
                topicChanges.map((change) => [change.id, change]),
            );
            const now = new Date().toISOString();

            return updateTopicsOptimistically(otherUserId, (topics) =>
                topics.map((topic) => {
                    const change = changesById.get(topic.id);
                    return change
                        ? applyTopicChange(topic, change, now)
                        : topic;
                }),
            );
        },
        onError: (_error, _topicChanges, topicsBeforeSave) =>
            restoreTopics(otherUserId, topicsBeforeSave),
        onSettled: () => refreshTopicsOnceEverySaveIsDone(otherUserId),
    });
}

function positionBelowEveryTopic(topics: UpcomingTopic[]): number {
    return topics.length > 0
        ? Math.max(...topics.map((topic) => topic.position)) +
              REBALANCED_SEPERATION_DISTANCE
        : 0;
}

async function updateTopicsOptimistically(
    otherUserId: number,
    update: (topics: UpcomingTopic[]) => UpcomingTopic[],
): Promise<UpcomingTopic[] | undefined> {
    const { queryKey } = upcomingTopicsQuery(otherUserId);
    await queryClient.cancelQueries({ queryKey });
    const topicsBeforeSave = queryClient.getQueryData(queryKey);
    queryClient.setQueryData(queryKey, (topics) => topics && update(topics));

    return topicsBeforeSave;
}

function restoreTopics(
    otherUserId: number,
    topicsBeforeSave: UpcomingTopic[] | undefined,
) {
    queryClient.setQueryData(
        upcomingTopicsQuery(otherUserId).queryKey,
        topicsBeforeSave,
    );
}

/*
 * A refetch while another save is still in flight would briefly bring back the
 * topics as they were before that save, so only the last one to finish refetches.
 */
function refreshTopicsOnceEverySaveIsDone(otherUserId: number) {
    if (
        queryClient.isMutating({
            mutationKey: SAVE_UPCOMING_TOPICS_MUTATION_KEY,
        }) > 1
    ) {
        return;
    }

    return queryClient.invalidateQueries({
        queryKey: upcomingTopicsQuery(otherUserId).queryKey,
    });
}

function applyTopicChange(
    topic: UpcomingTopic,
    change: UpcomingTopicChange,
    now: string,
): UpcomingTopic {
    return {
        ...topic,
        content: change.content ?? topic.content,
        position: change.position ?? topic.position,
        completed_at:
            change.is_completed === undefined
                ? topic.completed_at
                : change.is_completed
                  ? (topic.completed_at ?? now)
                  : null,
    };
}
